import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  LinearProgress,
  Divider,
  Avatar,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  ShowChart as ChartIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { finanzasService } from "../../services/finanzasService";
import {
  formatDateShort,
  getCurrentYear,
  getCurrentMonth,
} from "../../utils/dateUtils";

const dark = {
  bg: "rgba(19,24,41,0.95)",
  paper: "rgba(30,37,60,0.95)",
  card: "rgba(35,43,70,0.9)",
  border: "rgba(99,115,210,0.25)",
  text: "#e2e8f8",
  sub: "rgba(200,210,240,0.55)",
};

const fmt = (v) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
  }).format(v || 0);

const pct = (v) => `${(+v || 0).toFixed(1)}%`;

const colorPct = (v) => {
  const n = +v || 0;
  if (n >= 20) return "#4ade80";
  if (n >= 10) return "#facc15";
  if (n >= 0) return "#fb923c";
  return "#f87171";
};

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const KpiCard = ({ label, value, sub, gradient, icon: Icon }) => (
  <Box
    sx={{
      width: { xs: 128, sm: 150 },
      height: { xs: 128, sm: 150 },
      borderRadius: "50%",
      background: gradient,
      boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: { xs: 0.3, sm: 0.4 },
      mx: "auto",
      transition: "transform .2s",
      "&:hover": { transform: "scale(1.06)" },
    }}
  >
    <Icon sx={{ fontSize: { xs: 22, sm: 26 }, color: "rgba(255,255,255,0.9)" }} />
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: { xs: "1.05rem", sm: "1.25rem" },
        color: "#fff",
        lineHeight: 1,
        textAlign: "center",
        px: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.52rem", sm: "0.62rem" },
        color: "rgba(255,255,255,0.85)",
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        textAlign: "center",
      }}
    >
      {label}
    </Typography>
    {sub && (
      <Typography
        sx={{
          fontSize: { xs: "0.5rem", sm: "0.58rem" },
          color: "rgba(255,255,255,0.65)",
          textAlign: "center",
          px: 1,
        }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

const IndicatorCard = ({ label, value, desc, color }) => (
  <Paper
    sx={{
      p: 2.5,
      bgcolor: dark.card,
      border: `1px solid ${dark.border}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 2,
    }}
  >
    <Typography
      variant="caption"
      sx={{ color: dark.sub, textTransform: "uppercase", letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography variant="h4" sx={{ color, fontWeight: 800, my: 1 }}>
      {value}
    </Typography>
    <LinearProgress
      variant="determinate"
      value={Math.min(Math.abs(parseFloat(value) || 0), 100)}
      sx={{
        height: 6,
        borderRadius: 3,
        mb: 1,
        bgcolor: "rgba(255,255,255,0.08)",
        "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 },
      }}
    />
    <Typography variant="caption" sx={{ color: dark.sub }}>
      {desc}
    </Typography>
  </Paper>
);

const DataTable = ({ title, rows, accent, keyMap }) => (
  <Paper
    sx={{
      bgcolor: dark.paper,
      border: `1px solid ${dark.border}`,
      borderRadius: 2,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${dark.border}`,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: accent }}
      />
      <Typography
        variant="subtitle2"
        sx={{ color: dark.text, fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Chip
        label={rows.length}
        size="small"
        sx={{
          ml: "auto",
          bgcolor: accent + "30",
          color: accent,
          fontWeight: 700,
        }}
      />
    </Box>
    <TableContainer sx={{ maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {keyMap.map((k) => (
              <TableCell
                key={k.key}
                align={k.align || "left"}
                sx={{
                  bgcolor: dark.card,
                  color: dark.sub,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  borderBottom: `1px solid ${dark.border}`,
                  letterSpacing: 0.4,
                }}
              >
                {k.label.toUpperCase()}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={keyMap.length}
                align="center"
                sx={{ py: 4, color: dark.sub, bgcolor: dark.paper }}
              >
                Sin registros en este período
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, i) => (
              <TableRow
                key={row.id || i}
                sx={{
                  "&:hover": { bgcolor: "rgba(99,115,210,0.08)" },
                  "& td": {
                    borderBottom: `1px solid ${dark.border}`,
                    color: dark.text,
                    bgcolor: "transparent",
                  },
                }}
              >
                {keyMap.map((k) => (
                  <TableCell key={k.key} align={k.align || "left"}>
                    {k.render ? k.render(row[k.key], row) : (row[k.key] ?? "—")}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const FinanzasDashboard = () => {
  const [periodo, setPeriodo] = useState("mensual");
  const [year, setYear] = useState(getCurrentYear());
  const [month, setMonth] = useState(getCurrentMonth());
  const [quarter, setQuarter] = useState(1);
  const [resumen, setResumen] = useState(null);
  const [ingresos, setIngresos] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [rentabilidad, setRentabilidad] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    cargarDatos();
  }, [periodo, year, month, quarter]); // eslint-disable-line

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError("");
      const m = periodo === "mensual" ? month : null;
      const q = periodo === "trimestral" ? quarter : null;
      const [resRes, ingRes, egrRes, rentRes] = await Promise.allSettled([
        finanzasService.getResumenEjecutivo(periodo, year, m, q),
        finanzasService.getDetalleIngresos(periodo, year, m, q),
        finanzasService.getDetalleEgresos(periodo, year, m, q),
        finanzasService.getAnalisisRentabilidad(periodo, year, m, q),
      ]);
      if (resRes.status === "fulfilled")
        setResumen(resRes.value?.data || resRes.value || {});
      if (ingRes.status === "fulfilled") {
        const v = ingRes.value;
        setIngresos(
          Array.isArray(v?.data) ? v.data : Array.isArray(v) ? v : [],
        );
      }
      if (egrRes.status === "fulfilled") {
        const v = egrRes.value;
        setEgresos(Array.isArray(v?.data) ? v.data : Array.isArray(v) ? v : []);
      }
      if (rentRes.status === "fulfilled")
        setRentabilidad(rentRes.value?.data || rentRes.value || {});
    } catch (err) {
      setError("Error al cargar datos financieros");
    } finally {
      setLoading(false);
    }
  };

  const r = resumen || {};
  const rent = rentabilidad || {};
  const ingrTot = +(r.ingresos_totales || 0);
  const egrTot = +(r.egresos_totales || 0);
  const utilidad = +(r.utilidad_neta != null
    ? r.utilidad_neta
    : ingrTot - egrTot);
  const margenNeto = +(
    r.margen_neto || (ingrTot > 0 ? (utilidad / ingrTot) * 100 : 0)
  );
  const roi = +(rent.roi || (egrTot > 0 ? (utilidad / egrTot) * 100 : 0));
  const margenBruto = +(rent.margen_bruto || margenNeto);

  const periodoLabel =
    periodo === "mensual"
      ? `${MESES[month - 1]} ${year}`
      : periodo === "trimestral"
        ? `Q${quarter} ${year}`
        : `Año ${year}`;

  const selectSx = {
    bgcolor: dark.card,
    color: dark.text,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: dark.border },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(99,115,210,0.5)",
    },
    "& .MuiSvgIcon-root": { color: dark.sub },
  };

  return (
    <Box sx={{ bgcolor: dark.bg, minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{ bgcolor: "rgba(99,115,210,0.3)", width: 48, height: 48 }}
          >
            <ChartIcon sx={{ color: "#7c8eff" }} />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ color: dark.text, fontWeight: 700 }}>
              Finanzas
            </Typography>
            <Typography variant="caption" sx={{ color: dark.sub }}>
              {periodoLabel} · {ingresos.length + egresos.length} registros
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={
            loading ? (
              <CircularProgress size={16} sx={{ color: "#7c8eff" }} />
            ) : (
              <RefreshIcon />
            )
          }
          onClick={cargarDatos}
          disabled={loading}
          sx={{
            borderColor: dark.border,
            color: "#7c8eff",
            "&:hover": {
              borderColor: "#7c8eff",
              bgcolor: "rgba(99,115,210,0.1)",
            },
          }}
        >
          {loading ? "Cargando…" : "Actualizar"}
        </Button>
      </Box>

      <Paper
        sx={{
          bgcolor: dark.paper,
          border: `1px solid ${dark.border}`,
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: dark.sub }}>Período</InputLabel>
              <Select
                value={periodo}
                label="Período"
                onChange={(e) => {
                  setPeriodo(e.target.value);
                  setMonth(1);
                  setQuarter(1);
                }}
                sx={selectSx}
              >
                <MenuItem value="mensual">Mensual</MenuItem>
                <MenuItem value="trimestral">Trimestral (Q)</MenuItem>
                <MenuItem value="anual">Anual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel sx={{ color: dark.sub }}>Año</InputLabel>
              <Select
                value={year}
                label="Año"
                onChange={(e) => setYear(e.target.value)}
                sx={selectSx}
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {periodo === "mensual" && (
            <Grid item xs={12} sm="auto">
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ color: dark.sub }}>Mes</InputLabel>
                <Select
                  value={month}
                  label="Mes"
                  onChange={(e) => setMonth(e.target.value)}
                  sx={selectSx}
                >
                  {MESES.map((m, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {periodo === "trimestral" && (
            <Grid item xs={12} sm="auto">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ color: dark.sub }}>Trimestre</InputLabel>
                <Select
                  value={quarter}
                  label="Trimestre"
                  onChange={(e) => setQuarter(e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value={1}>Q1 — Ene·Mar</MenuItem>
                  <MenuItem value={2}>Q2 — Abr·Jun</MenuItem>
                  <MenuItem value={3}>Q3 — Jul·Sep</MenuItem>
                  <MenuItem value={4}>Q4 — Oct·Dic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {error && (
        <Paper
          sx={{
            bgcolor: "rgba(239,68,68,0.12)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography sx={{ color: "#f87171", fontSize: 14 }}>
            {error}
          </Typography>
        </Paper>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center" sx={{ mb: 3 }}>
        <Grid item xs={6} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <KpiCard
            label="Ingresos"
            value={fmt(ingrTot)}
            sub={`${ingresos.length} eventos`}
            gradient="linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%)"
            icon={MoneyIcon}
          />
        </Grid>
        <Grid item xs={6} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <KpiCard
            label="Egresos"
            value={fmt(egrTot)}
            sub={`${egresos.length} gastos`}
            gradient="linear-gradient(135deg,#ec4899 0%,#9d174d 100%)"
            icon={TrendingDownIcon}
          />
        </Grid>
        <Grid item xs={6} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <KpiCard
            label="Utilidad Neta"
            value={fmt(utilidad)}
            sub={utilidad >= 0 ? "Positiva ✓" : "Negativa ✗"}
            gradient={
              utilidad >= 0
                ? "linear-gradient(135deg,#10b981 0%,#065f46 100%)"
                : "linear-gradient(135deg,#ef4444 0%,#7f1d1d 100%)"
            }
            icon={TrendingUpIcon}
          />
        </Grid>
        <Grid item xs={6} sm="auto" sx={{ display: "flex", justifyContent: "center" }}>
          <KpiCard
            label="ROI"
            value={pct(roi)}
            sub={
              roi >= 20
                ? "Excelente"
                : roi >= 10
                  ? "Bueno"
                  : roi >= 0
                    ? "Bajo"
                    : "Pérdida"
            }
            gradient="linear-gradient(135deg,#8b5cf6 0%,#4c1d95 100%)"
            icon={ChartIcon}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <IndicatorCard
            label="Margen Bruto"
            value={pct(margenBruto)}
            desc="Ganancia antes de gastos operacionales"
            color={colorPct(margenBruto)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <IndicatorCard
            label="Margen Neto"
            value={pct(margenNeto)}
            desc="Porcentaje de ganancia final sobre ingresos"
            color={colorPct(margenNeto)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <IndicatorCard
            label="ROI"
            value={pct(roi)}
            desc="Retorno sobre inversión realizada"
            color={colorPct(roi)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <DataTable
            title="Ingresos del período"
            rows={ingresos}
            accent="#4ade80"
            keyMap={[
              { key: "descripcion", label: "Descripción" },
              {
                key: "fecha",
                label: "Fecha",
                render: (v) => (v ? formatDateShort(v) : "—"),
              },
              {
                key: "estatus",
                label: "Estado",
                render: (v) =>
                  v ? (
                    <Chip
                      label={v}
                      size="small"
                      sx={{
                        bgcolor: "rgba(74,222,128,0.15)",
                        color: "#4ade80",
                        fontSize: "0.7rem",
                      }}
                    />
                  ) : (
                    "—"
                  ),
              },
              {
                key: "monto",
                label: "Monto",
                align: "right",
                render: (v) => (
                  <span style={{ color: "#4ade80", fontWeight: 700 }}>
                    {fmt(v)}
                  </span>
                ),
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <DataTable
            title="Egresos del período"
            rows={egresos}
            accent="#f87171"
            keyMap={[
              { key: "descripcion", label: "Descripción" },
              {
                key: "fecha",
                label: "Fecha",
                render: (v) => (v ? formatDateShort(v) : "—"),
              },
              {
                key: "referencia",
                label: "Categoría",
                render: (v) =>
                  v ? (
                    <Chip
                      label={v}
                      size="small"
                      sx={{
                        bgcolor: "rgba(248,113,113,0.15)",
                        color: "#f87171",
                        fontSize: "0.7rem",
                      }}
                    />
                  ) : (
                    "—"
                  ),
              },
              {
                key: "monto",
                label: "Monto",
                align: "right",
                render: (v) => (
                  <span style={{ color: "#f87171", fontWeight: 700 }}>
                    {fmt(v)}
                  </span>
                ),
              },
            ]}
          />
        </Grid>
      </Grid>

      <Paper
        sx={{
          bgcolor: dark.paper,
          border: `1px solid ${dark.border}`,
          borderRadius: 2,
          p: 2,
        }}
      >
        <Grid
          container
          spacing={2}
          justifyContent="space-around"
          alignItems="center"
        >
          <Grid item sx={{ textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: dark.sub }}>
              INGRESOS
            </Typography>
            <Typography variant="h6" sx={{ color: "#4ade80", fontWeight: 700 }}>
              {fmt(ingrTot)}
            </Typography>
          </Grid>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: dark.border }}
          />
          <Grid item sx={{ textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: dark.sub }}>
              EGRESOS
            </Typography>
            <Typography variant="h6" sx={{ color: "#f87171", fontWeight: 700 }}>
              {fmt(egrTot)}
            </Typography>
          </Grid>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: dark.border }}
          />
          <Grid item sx={{ textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: dark.sub }}>
              UTILIDAD
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: utilidad >= 0 ? "#4ade80" : "#f87171",
                fontWeight: 700,
              }}
            >
              {fmt(utilidad)}
            </Typography>
          </Grid>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: dark.border }}
          />
          <Grid item sx={{ textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: dark.sub }}>
              MARGEN
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: colorPct(margenNeto), fontWeight: 700 }}
            >
              {pct(margenNeto)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default FinanzasDashboard;
